// PanelDimsDialog.tsx — Full panel dimension form (mpvar group)
// Maps to v11 pnl_dlg / drawing setup dimensions
// Fields: panelMark, width, height, thickness, topElev, botElev,
//         projectName, jobNumber, drawingNumber, drawBy, checkBy, revision, date

import { useState } from 'react';
import DialogBase from './DialogBase';
import { useShallowStore } from '../store/useStore';
import type { MpVar } from '../types/panelvar';

export default function PanelDimsDialog() {
  const { ui, project, updateMpVar } = useShallowStore(s => ({
    ui:          s.ui,
    project:     s.project,
    updateMpVar: s.updateMpVar,
  }));

  const panel = project?.panels.find(p => p.id === ui.activePanelId);
  const [mp, setMp] = useState<MpVar>(panel?.mpvar ?? ({} as MpVar));

  if (!panel) return null;

  function set<K extends keyof MpVar>(key: K, value: MpVar[K]) {
    setMp(prev => ({ ...prev, [key]: value }));
  }

  function nRow(label: string, key: keyof MpVar, unit = '"', step = 0.5) {
    return (
      <div className="dialog-row" key={key as string}>
        <label className="dialog-label">{label}</label>
        <input
          className="dialog-input sm"
          type="number"
          value={mp[key] as number}
          step={step}
          onChange={e => set(key, parseFloat(e.target.value) as MpVar[typeof key])}
        />
        <span className="dialog-unit">{unit}</span>
      </div>
    );
  }

  function tRow(label: string, key: keyof MpVar) {
    return (
      <div className="dialog-row" key={key as string}>
        <label className="dialog-label">{label}</label>
        <input
          className="dialog-input"
          type="text"
          value={mp[key] as string}
          onChange={e => set(key, e.target.value as MpVar[typeof key])}
        />
      </div>
    );
  }

  return (
    <DialogBase
      title="Panel Settings"
      onOk={() => updateMpVar(panel.id, mp)}
      width={520}
    >
      <div className="dialog-section-title">Drawing Info</div>
      <div className="dialog-row">
        <label className="dialog-label">Project Name</label>
        <input className="dialog-input" type="text" value={project?.name ?? ''} disabled />
      </div>
      <div className="dialog-row">
        <label className="dialog-label">Job Number</label>
        <input className="dialog-input" type="text" value={project?.jobNumber ?? ''} disabled />
      </div>
      {tRow('Drawing Number',  'drawingNumber')}
      {tRow('Drawn By',        'drawBy')}
      {tRow('Checked By',      'checkBy')}
      {tRow('Revision',        'revision')}
      {tRow('Date',            'date')}

      <div className="dialog-section-title">Quantities / Notes</div>
      {nRow('Quantity',  'quantity', 'pcs', 1)}
      {tRow('Finish',    'finish')}
      {tRow('Color',     'color')}
      {tRow('Notes',     'notes')}
    </DialogBase>
  );
}
